import {
  type GetAllArtifactsQuery as GetAllArtifactsQueryResult,
  type RootState,
  apiSlice,
  gqlApiSlice,
} from '@fly/store';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';

import {
  getAllArtifactsQuery,
  getArtifactDetailsQuery,
  getArtifactFilesQuery,
  getRegistryInsightsQuery,
  getRepositoriesQuery,
  getRepositoryNamesQuery,
  getRepositoryReleaseQuery,
  getRepositoryReleasesQuery,
  getRepositoryWorkflowsQuery,
  getWorkflowNamesQuery,
} from './registry.queries';
import {
  GetArtifactFilesResult,
  GetArtifactFilesVariables,
  GetRepositoryReleasesResult,
  GetRepositoryReleasesVariables,
  GetWorkflowNamesResult,
  GetWorkflowNamesVariables,
  RepositoryReleaseResult as TransformerRepositoryReleaseResult,
  transformArtifactDetails,
  transformArtifactFiles,
  transformArtifacts,
  transformRegistryInsights,
  transformRepositories,
  transformRepositoryNames,
  transformRepositoryRelease,
  transformRepositoryReleases,
  transformRepositoryWorkflows,
  transformWorkflowNames,
} from './registry.transformers';

// Type exports (from v2 query results)
type GetRepositoryNamesQueryResult = ResultOf<typeof getRepositoryNamesQuery>;
export type RepositoryName =
  GetRepositoryNamesQueryResult['flyGitRepositories']['searchRepositories']['edges'][number]['node'];

export type GetRepositoriesQueryVariables = VariablesOf<
  typeof getRepositoriesQuery
>;
export type Repository = ReturnType<typeof transformRepositories>[number];

type GetRegistryInsightsQueryResult = ResultOf<typeof getRegistryInsightsQuery>;
export type RegistryInsightsInput = VariablesOf<
  typeof getRegistryInsightsQuery
>;
export type RegistryInsights = NonNullable<
  GetRegistryInsightsQueryResult['flyOperationalInfo']['getRegistryInsights']
>;

export const ReleaseStatus = {
  SUCCESS: 'success',
  FAILURE: 'failure',
  CANCELLED: 'cancelled',
} as const;
export type ReleaseStatus = (typeof ReleaseStatus)[keyof typeof ReleaseStatus];

export type GetRepositoryWorkflowsQueryVariables = VariablesOf<
  typeof getRepositoryWorkflowsQuery
>;
export type RepositoryWithWorkflows = ReturnType<
  typeof transformRepositoryWorkflows
>;
export type RepositoryWorkflow = RepositoryWithWorkflows['workflows'][number];

export type GetRepositoryReleaseQueryVariables = VariablesOf<
  typeof getRepositoryReleaseQuery
>;
export type RepositoryReleaseResult = TransformerRepositoryReleaseResult;
export type RepositoryWorkflowRelease =
  TransformerRepositoryReleaseResult[number];
export type ReleaseEntry = RepositoryWorkflowRelease['releases'][number];
export type RepositoryReleaseDetail = ReleaseEntry & {
  workflowName: string;
  fullName: string;
  status: ReleaseStatus;
};
export type RepositoryPullRequest =
  RepositoryReleaseDetail['pullRequests'][number];
export type RepositoryReleaseArtifact =
  RepositoryWorkflowRelease['releases'][number]['uploadedArtifacts'][number];
export type RepositoryCommit = RepositoryReleaseDetail['commits'][number];

export type AllArtifactsInput = VariablesOf<typeof getAllArtifactsQuery>;
type AllArtifactsNode =
  GetAllArtifactsQueryResult['flyArtifacts']['searchArtifacts']['edges'][number]['node'];
export type AllArtifacts = AllArtifactsNode[];
export type AllArtactsArtifact = AllArtifacts[number];
/** Unified artifact row type for both All Artifacts and repository-scoped Artifacts table. */
export type ArtifactPageItem = AllArtifacts[number];

export type GetAllArtifactsPageInfo =
  GetAllArtifactsQueryResult['flyArtifacts']['searchArtifacts']['pageInfo'];
export type GetAllArtifactsPageResult = {
  data: AllArtifacts;
  pageInfo: GetAllArtifactsPageInfo;
};

export type GetArtifactDetailsQueryVariables = VariablesOf<
  typeof getArtifactDetailsQuery
>;
type GetArtifactDetailsQueryResult = ResultOf<typeof getArtifactDetailsQuery>;
export type GetArtifactDetailsQuery = GetArtifactDetailsQueryResult;
export type ArtifactDetails = ReturnType<typeof transformArtifactDetails>;
export type ArtifactVersion = ArtifactDetails[number]['versions'][number];
/** Present on a version when it has been distributed via Fly generic distribution. */
export type ArtifactVersionDistribution = NonNullable<
  ArtifactVersion['distribution']
>;

const artifactFilesTagId = ({
  name,
  packageVersion,
}: {
  name: string;
  packageVersion: string;
}) => `${name}::${packageVersion}`;

export const registryService = gqlApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRepositoryNames: builder.query<RepositoryName[], void>({
      query: () => ({
        document: getRepositoryNamesQuery,
      }),
      transformResponse: transformRepositoryNames,
      providesTags: ['repository-names'],
    }),
    getRepositories: builder.query<
      Repository[],
      { refreshCache?: boolean; owner?: string; name?: string } | void
    >({
      query: (args) => ({
        document: getRepositoriesQuery,
        variables: {
          refreshCache: args?.refreshCache,
          owner: args?.owner,
          name: args?.name,
        },
      }),
      transformResponse: (response: ResultOf<typeof getRepositoriesQuery>) =>
        transformRepositories(response),
      providesTags: ['repositories'],
    }),
    getAllArtifacts: builder.infiniteQuery<
      GetAllArtifactsPageResult,
      Partial<AllArtifactsInput>,
      string | null
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) =>
          lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : null,
        getPreviousPageParam: () => null,
        initialPageParam: null,
      },
      query: ({ queryArg, pageParam }) => {
        const v: Partial<AllArtifactsInput> | undefined = queryArg ?? undefined;
        return {
          document: getAllArtifactsQuery,
          variables: {
            search: v?.search,
            packageTypes: v?.packageTypes,
            names: v?.names,
            first: v?.first ?? 50,
            ...(pageParam ? { after: pageParam } : {}),
            repositoryOwner: v?.repositoryOwner,
            repositoryName: v?.repositoryName,
          },
        };
      },
      transformResponse: (response: unknown): GetAllArtifactsPageResult => {
        const r = response as GetAllArtifactsQueryResult;
        const pageInfo: GetAllArtifactsPageInfo = r?.flyArtifacts
          ?.searchArtifacts?.pageInfo ?? {
          hasNextPage: false,
          endCursor: null,
        };
        return {
          data: transformArtifacts(r),
          pageInfo,
        };
      },
      providesTags: ['artifacts'],
    }),
    getRegistryInsights: builder.query<RegistryInsights, void>({
      query: () => ({
        document: getRegistryInsightsQuery,
      }),
      transformResponse: (
        response: ResultOf<typeof getRegistryInsightsQuery>
      ) => transformRegistryInsights(response),
    }),
    getRepositoryWorkflows: builder.query<
      RepositoryWithWorkflows,
      GetRepositoryWorkflowsQueryVariables
    >({
      query: (variables) => ({
        document: getRepositoryWorkflowsQuery,
        variables,
      }),
      transformResponse: (
        response: ResultOf<typeof getRepositoryWorkflowsQuery>
      ) => transformRepositoryWorkflows(response),
      providesTags: ['repository-workflows'],
    }),
    getRepositoryRelease: builder.query<
      TransformerRepositoryReleaseResult,
      GetRepositoryReleaseQueryVariables
    >({
      query: (variables) => ({
        document: getRepositoryReleaseQuery,
        variables,
      }),
      transformResponse: transformRepositoryRelease,
      providesTags: (
        result,
        error,
        { owner, name, workflowName, workflowRunNumber }
      ) => [
        {
          type: 'repository-release',
          id: `${owner}-${name}-${workflowName}-${workflowRunNumber}`,
        },
      ],
      keepUnusedDataFor: 300,
      serializeQueryArgs: ({ queryArgs }) => {
        const { owner, name, workflowName, workflowRunNumber } = queryArgs;
        return `${owner}-${name}-${workflowName}-${workflowRunNumber}`;
      },
    }),
    getArtifactVersions: builder.infiniteQuery<
      ArtifactDetails,
      GetArtifactDetailsQueryVariables,
      string | null
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) => {
          const pageInfo = lastPage[0]?.versionsPageInfo;
          return pageInfo?.hasNextPage ? pageInfo.endCursor : null;
        },
        getPreviousPageParam: () => null,
        initialPageParam: null,
      },
      query: ({ queryArg, pageParam }) => ({
        document: getArtifactDetailsQuery,
        variables: {
          name: queryArg.name,
          packageType: queryArg.packageType,
          first: queryArg.first ?? 1,
          versionsFirst: queryArg.versionsFirst ?? 50,
          ...(pageParam ? { versionsAfter: pageParam } : {}),
          versionsSearch: queryArg.versionsSearch,
          releasesFirst: queryArg.releasesFirst,
        },
      }),
      transformResponse: transformArtifactDetails,
      providesTags: ['artifact-details'],
    }),
    getArtifactFiles: builder.infiniteQuery<
      GetArtifactFilesResult,
      GetArtifactFilesVariables,
      string | null
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) =>
          lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : null,
        getPreviousPageParam: () => null,
        initialPageParam: null,
      },
      query: ({ queryArg, pageParam }) => ({
        document: getArtifactFilesQuery,
        variables: {
          name: queryArg.name,
          packageVersion: queryArg.packageVersion,
          packageType: queryArg.packageType,
          first: queryArg.first ?? 10,
          ...(pageParam ? { after: pageParam } : {}),
        },
      }),
      transformResponse: transformArtifactFiles,
      providesTags: (_result, _error, queryArg) => [
        {
          type: 'artifact-files',
          id: artifactFilesTagId(queryArg),
        },
      ],
    }),
    getRepositoryReleases: builder.infiniteQuery<
      GetRepositoryReleasesResult,
      GetRepositoryReleasesVariables,
      string | null
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage, pages) =>
          lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : null,
        getPreviousPageParam: () => null,
        initialPageParam: null,
      },
      query: ({ queryArg, pageParam }) => ({
        document: getRepositoryReleasesQuery,
        variables: {
          ...queryArg,
          ...(pageParam ? { after: pageParam } : {}),
        },
      }),
      transformResponse: transformRepositoryReleases,
      providesTags: (_result, _error, { repository }) => [
        { type: 'repository-releases', id: repository },
      ],
    }),
    getWorkflowNames: builder.query<
      GetWorkflowNamesResult,
      GetWorkflowNamesVariables
    >({
      query: (variables) => ({
        document: getWorkflowNamesQuery,
        variables,
      }),
      transformResponse: transformWorkflowNames,
    }),
  }),
});

/** JSON body returned from POST /artifacts/distribute (matches Go distributeResponse). */
export type DistributeArtifactResponse = {
  package_name: string;
  package_version: string;
  package_type: string;
};

/** Tags to refresh after package/distribution REST mutations succeed. */
const DISTRIBUTION_MUTATION_INVALIDATION_TAGS = [
  'repositories',
  'artifact-details',
  'artifacts',
  'repository-workflows',
  'repository-release',
  'repository-releases',
] as const;

// REST service for mutations
export const registryRestService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    editRelease: builder.mutation<
      void,
      {
        id: string;
        name: string;
        description?: string;
        owner: string;
        repoName: string;
        workflowName: string;
        workflowRunNumber: string;
      }
    >({
      query: ({ id, name, description }) => ({
        url: `/releases/${id}`,
        method: 'PATCH',
        data: {
          release_title: name,
          ...(description ? { release_short_summary: description } : {}),
        },
      }),
      onQueryStarted: (
        {
          id,
          name,
          description,
          owner,
          repoName,
          workflowName,
          workflowRunNumber,
        },
        { queryFulfilled, dispatch, getState }
      ) => {
        const updateRun = (run: {
          title?: string | null;
          shortSummary?: string | null;
        }) => {
          run.title = name;
          if (description !== undefined) {
            run.shortSummary = description;
          }
        };

        const releasePatch = dispatch(
          registryService.util.updateQueryData(
            'getRepositoryRelease',
            { owner, name: repoName, workflowName, workflowRunNumber },
            (draft) => {
              const workflow = draft.find((w) => w.name === workflowName);
              const run = workflow?.releases.find((r) => r.id === id);
              if (run) updateRun(run);
            }
          )
        );

        const repositoryReleasesPatches = registryService.util
          .selectInvalidatedBy(getState() as RootState, [
            { type: 'repository-releases', id: `${owner}/${repoName}` },
          ])
          .filter(
            ({ endpointName }) => endpointName === 'getRepositoryReleases'
          )
          .map(({ originalArgs }) =>
            dispatch(
              registryService.util.updateQueryData(
                'getRepositoryReleases',
                originalArgs as GetRepositoryReleasesVariables,
                (draft) => {
                  for (const page of draft.pages) {
                    const run = page.releases.find((r) => r.id === id);
                    if (run) {
                      updateRun(run);
                      break;
                    }
                  }
                }
              )
            )
          );

        queryFulfilled.catch(() => {
          releasePatch.undo();
          repositoryReleasesPatches.forEach((p) => p.undo());
        });

        queryFulfilled.then(() => {
          dispatch(
            gqlApiSlice.util.invalidateTags([
              'environments',
              { type: 'repository-releases', id: `${owner}/${repoName}` },
            ])
          );
        });
      },
    }),

    deleteArtifactVersion: builder.mutation<
      void,
      {
        packageName: string;
        packageVersion: string;
        packageType: string;
      }
    >({
      query: ({ packageName, packageVersion, packageType }) => ({
        url: '/packages',
        method: 'DELETE',
        data: {
          package_name: packageName,
          package_version: packageVersion,
          package_type: packageType,
        },
      }),
      onQueryStarted: (
        { packageName, packageVersion },
        { queryFulfilled, dispatch }
      ) => {
        queryFulfilled.then(() => {
          dispatch(
            gqlApiSlice.util.invalidateTags([
              ...DISTRIBUTION_MUTATION_INVALIDATION_TAGS,
              {
                type: 'artifact-files',
                id: artifactFilesTagId({
                  name: packageName,
                  packageVersion,
                }),
              },
            ])
          );
        });
      },
    }),

    distributeArtifact: builder.mutation<
      DistributeArtifactResponse,
      {
        packageName: string;
        packageVersion: string;
        packageType: string;
      }
    >({
      query: ({ packageName, packageVersion, packageType }) => ({
        url: '/artifacts/distribute',
        method: 'POST',
        data: {
          package_name: packageName,
          package_version: packageVersion,
          package_type: packageType,
        },
      }),
      onQueryStarted: (
        { packageName, packageVersion },
        { queryFulfilled, dispatch }
      ) => {
        queryFulfilled.then(() => {
          dispatch(
            gqlApiSlice.util.invalidateTags([
              ...DISTRIBUTION_MUTATION_INVALIDATION_TAGS,
              {
                type: 'artifact-files',
                id: artifactFilesTagId({
                  name: packageName,
                  packageVersion,
                }),
              },
            ])
          );
        });
      },
    }),

    /** POST /artifacts/revoke — success is HTTP 204 No Content (no JSON body). */
    distributeArtifactRevoke: builder.mutation<
      void,
      {
        packageName: string;
        packageVersion: string;
        packageType: string;
      }
    >({
      query: ({ packageName, packageVersion, packageType }) => ({
        url: '/artifacts/revoke',
        method: 'POST',
        data: {
          package_name: packageName,
          package_version: packageVersion,
          package_type: packageType,
        },
      }),
      onQueryStarted: (
        { packageName, packageVersion },
        { queryFulfilled, dispatch }
      ) => {
        queryFulfilled.then(() => {
          dispatch(
            gqlApiSlice.util.invalidateTags([
              ...DISTRIBUTION_MUTATION_INVALIDATION_TAGS,
              {
                type: 'artifact-files',
                id: artifactFilesTagId({
                  name: packageName,
                  packageVersion,
                }),
              },
            ])
          );
        });
      },
    }),
  }),
});

export const {
  useGetRepositoryReleaseQuery,
  useGetArtifactVersionsInfiniteQuery,
  useGetRepositoryWorkflowsQuery,
  useGetRepositoryNamesQuery,
  useLazyGetRepositoriesQuery,
  useGetAllArtifactsInfiniteQuery,
  useGetArtifactFilesInfiniteQuery,
  useGetRegistryInsightsQuery,
  useGetRepositoryReleasesInfiniteQuery,
  useGetWorkflowNamesQuery,
} = registryService;

export const {
  useDeleteArtifactVersionMutation,
  useDistributeArtifactMutation,
  useDistributeArtifactRevokeMutation,
  useEditReleaseMutation,
} = registryRestService;
